import { BOARD_TEXT_FONT } from "./textSession";
import type { BoardObjectV2 } from "@bezpart-board/shared";
import { cloneSceneObjects } from "./objectClone";
import { roundPathCoord } from "./pathGeometry";
import { measureTextObjectSize } from "./textObject";

const CLIPBOARD_MIME = "application/x-bezpart-board-objects";
const PLAIN_PREFIX = "bezpart-board-objects:";
const MAX_PASTE_TEXT = 4000;

let internalClipboard: BoardObjectV2[] | null = null;
let pasteCount = 0;

/** Событие о смене буфера — по нему кнопка «вставить» узнаёт, что ей пора. */
const CLIPBOARD_EVENT = "wb-board-clipboard";

export function setInternalClipboard(objects: BoardObjectV2[]): void {
  internalClipboard = objects.map((o) => JSON.parse(JSON.stringify(o)) as BoardObjectV2);
  pasteCount = 0;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(CLIPBOARD_EVENT));
  }
}

export function hasInternalClipboard(): boolean {
  return Boolean(internalClipboard?.length);
}

/** Подписка для React: кнопка вставки появляется сразу после копирования. */
export function subscribeInternalClipboard(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => onChange();
  window.addEventListener(CLIPBOARD_EVENT, handler);
  return () => window.removeEventListener(CLIPBOARD_EVENT, handler);
}

/** Картинка из буфера (скрин Win+Shift+S, копирование файла и т.п.). */
export function imageFileFromClipboardData(data: DataTransfer | null | undefined): File | null {
  if (!data) return null;
  const items = data.items;
  if (items) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item || item.kind !== "file") continue;
      if (!item.type.startsWith("image/")) continue;
      const file = item.getAsFile();
      if (file) return file;
    }
  }
  const files = data.files;
  if (files) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file && file.type.startsWith("image/")) return file;
    }
  }
  return null;
}

function translateObjectsBy(objects: BoardObjectV2[], dx: number, dy: number): void {
  if (!dx && !dy) return;
  for (const obj of objects) {
    // 2 знака, как при рисовании: округление до целых портит рукописные штрихи.
    obj.x = roundPathCoord((obj.x || 0) + dx);
    obj.y = roundPathCoord((obj.y || 0) + dy);
    const pts = (obj as { points?: unknown }).points;
    if (!Array.isArray(pts)) continue;
    (obj as { points: unknown }).points = pts.map((p) => {
      if (!Array.isArray(p) || p.length < 2) return p;
      return [roundPathCoord(Number(p[0]) + dx), roundPathCoord(Number(p[1]) + dy)];
    });
  }
}

function objectsCenter(objects: BoardObjectV2[]): { x: number; y: number } | null {
  if (!objects.length) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const obj of objects) {
    const w = typeof obj.w === "number" ? obj.w : 0;
    const h = typeof obj.h === "number" ? obj.h : 0;
    minX = Math.min(minX, obj.x || 0);
    minY = Math.min(minY, obj.y || 0);
    maxX = Math.max(maxX, (obj.x || 0) + w);
    maxY = Math.max(maxY, (obj.y || 0) + h);
  }
  if (!Number.isFinite(minX)) return null;
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
}

export function preparePasteClones(
  allObjects: BoardObjectV2[],
  offsetStep = 16,
  anchor?: { x: number; y: number } | null
): BoardObjectV2[] | null {
  if (!internalClipboard?.length) return null;
  pasteCount += 1;
  const nudge = offsetStep * pasteCount;
  const clones = cloneSceneObjects(internalClipboard, allObjects, nudge);
  if (anchor && Number.isFinite(anchor.x) && Number.isFinite(anchor.y)) {
    const center = objectsCenter(clones);
    if (center) {
      translateObjectsBy(clones, Math.round(anchor.x - center.x), Math.round(anchor.y - center.y));
    }
  }
  return clones;
}

export function clipboardMimeType(): string {
  return CLIPBOARD_MIME;
}

export function serializeClipboard(objects: BoardObjectV2[]): string {
  return JSON.stringify({ v: 1, objects });
}

export function parseClipboardPayload(raw: string): BoardObjectV2[] | null {
  try {
    const data = JSON.parse(raw) as { v?: number; objects?: BoardObjectV2[] };
    if (data.v !== 1 || !Array.isArray(data.objects) || !data.objects.length) return null;
    return data.objects.map((o) => JSON.parse(JSON.stringify(o)) as BoardObjectV2);
  } catch {
    return null;
  }
}

export function loadClipboardFromPayload(objects: BoardObjectV2[]): void {
  setInternalClipboard(objects);
}

/** Запись объектов доски в системный буфер (межвкладочная вставка). */
export async function writeBoardClipboardToSystem(objects: BoardObjectV2[]): Promise<void> {
  if (!objects.length) return;
  const plain = PLAIN_PREFIX + serializeClipboard(objects);

  // Только text/plain — custom MIME в ClipboardItem часто роняет всю запись в Chrome.
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(plain);
      return;
    }
  } catch {
    /* ignore */
  }

  try {
    if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
    }
  } catch {
    /* internal clipboard всё равно работает в этой вкладке */
  }
}

/** Объекты доски из системного буфера (custom MIME или text/plain префикс). */
export function boardObjectsFromClipboardData(
  data: DataTransfer | null | undefined
): BoardObjectV2[] | null {
  if (!data) return null;

  try {
    const custom = data.getData(CLIPBOARD_MIME);
    if (custom) {
      const parsed = parseClipboardPayload(custom);
      if (parsed?.length) return parsed;
    }
  } catch {
    /* ignore */
  }

  try {
    const plain = data.getData("text/plain");
    if (plain?.startsWith(PLAIN_PREFIX)) {
      return parseClipboardPayload(plain.slice(PLAIN_PREFIX.length));
    }
  } catch {
    /* ignore */
  }

  return null;
}

/** Обычный текст из буфера (не payload объектов доски). */
export function plainTextFromClipboardData(
  data: DataTransfer | null | undefined
): string | null {
  if (!data) return null;
  let plain = "";
  try {
    plain = data.getData("text/plain") || "";
  } catch {
    return null;
  }
  plain = plain.replace(/\r\n/g, "\n").trim();
  if (!plain) return null;
  if (plain.startsWith(PLAIN_PREFIX)) return null;
  if (plain.length > MAX_PASTE_TEXT) plain = plain.slice(0, MAX_PASTE_TEXT);
  return plain;
}

/** Безопасный http(s) URL, если вся строка — ссылка. */
export function resolvePasteHref(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const lines = trimmed.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length !== 1) return null;

  let candidate = lines[0]!;
  if (/\s/.test(candidate)) return null;
  if (/^www\./i.test(candidate)) candidate = `https://${candidate}`;
  if (!/^https?:\/\//i.test(candidate)) return null;

  try {
    const u = new URL(candidate);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    return u.href;
  } catch {
    return null;
  }
}

/** Размер текстового объекта под вставляемый текст. */
export function estimateTextObjectSize(text: string): { w: number; h: number } {
  return measureTextObjectSize(text, {
    fontSize: 18,
    fontFamily: BOARD_TEXT_FONT,
    lineHeight: 1.35,
    fontWeight: "normal",
    fontStyle: "normal",
  });
}
